import { ref, computed } from 'vue';

/*
 * Bumped from v1 to v2 (D5 — PostHog added as new analytics sub-processor).
 * All existing members will be re-prompted to confirm consent at next reload.
 * See docs/features/posthog-instrumentation/usage.md — "Consent version bump procedure".
 */
const STORAGE_KEY = 'korum.consent.v2';
// 13 months in milliseconds (CNIL recommendation).
const TTL_MS = 13 * 30 * 24 * 60 * 60 * 1000;
const CONSENT_VERSION = 2 as const;

type ConsentCategory = 'necessary' | 'functional' | 'analytics' | 'marketing';

type ConsentSnapshot = {
  version: typeof CONSENT_VERSION;
  decidedAt: string;
  categories: Record<ConsentCategory, boolean>;
};

type UseConsent = {
  /** True when the user has made a choice (banner closed). */
  hasDecided: ReturnType<typeof computed<boolean>>;
  /** True when the category is allowed OR is strictly necessary. */
  hasConsent: (category: ConsentCategory) => boolean;
  /** Full reactive snapshot. */
  snapshot: ReturnType<typeof computed<ConsentSnapshot | null>>;
  /** Set consent for a category (has no effect on 'necessary'). */
  setConsent: (category: ConsentCategory, value: boolean) => void;
  /** Accept all categories. */
  acceptAll: () => void;
  /** Reject all non-necessary categories. */
  rejectAll: () => void;
  /** Force-open the preferences modal (e.g. "Manage cookies" link). */
  openPreferences: () => void;
  /** True when the initial banner should be shown. */
  shouldShowBanner: ReturnType<typeof computed<boolean>>;
  /** True when the preferences modal should be shown. */
  shouldShowPreferences: ReturnType<typeof computed<boolean>>;
  /** Close the preferences modal without changing choices. */
  closePreferences: () => void;
};

// Singleton state — shared across all consumers in the same app instance.
const _snapshot = ref<ConsentSnapshot | null>(null);
const _showBanner = ref(false);
const _showPreferences = ref(false);

/**
 * Reads and validates the stored consent snapshot from localStorage.
 * Returns null if absent, expired, or version-mismatched.
 */
const readStorage = (): ConsentSnapshot | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentSnapshot | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    // A truncated or hand-edited value must not reach hasConsent(): it is
    // called from boot/analytics.ts, and a throw inside a boot file aborts
    // Quasar's boot chain before app.mount(), leaving the page unhydrated.
    if (!parsed.categories || typeof parsed.categories !== 'object') return null;
    const age = Date.now() - new Date(parsed.decidedAt).getTime();
    if (Number.isNaN(age) || age > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Persists the snapshot to localStorage and dispatches a consent-change event.
 */
const writeStorage = (snap: ConsentSnapshot): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  window.dispatchEvent(new CustomEvent('korum:consent-change', { detail: snap }));
};

/**
 * Builds a fresh snapshot with the given categories.
 */
const buildSnapshot = (categories: Record<ConsentCategory, boolean>): ConsentSnapshot => ({
  version: CONSENT_VERSION,
  decidedAt: new Date().toISOString(),
  categories
});

/**
 * Composable for cookie consent management.
 *
 * Implements the useConsent interface defined in the legal-2026-05-17 spec (§6.5).
 * Reads/writes korum.consent.v2 in localStorage. SSR-safe.
 * Exposes hasConsent() as a gate for future SDK integrations.
 *
 * Version history:
 *   v1 — initial consent (Vercel Analytics).
 *   v2 — bumped to re-prompt all members after PostHog added as new sub-processor (D5).
 */
export const useConsent = (): UseConsent => {
  // Hydrate on first call.
  if (_snapshot.value === null && typeof window !== 'undefined') {
    const stored = readStorage();
    if (stored) {
      _snapshot.value = stored;
      _showBanner.value = false;
    } else {
      _showBanner.value = true;
    }
  }

  const hasDecided = computed(() => _snapshot.value !== null);

  const snapshot = computed(() => _snapshot.value);

  const hasConsent = (category: ConsentCategory): boolean => {
    if (category === 'necessary') return true;
    if (!_snapshot.value) return false;
    return _snapshot.value.categories[category] ?? false;
  };

  const setConsent = (category: ConsentCategory, value: boolean): void => {
    if (category === 'necessary') return;
    const current = _snapshot.value?.categories ?? {
      necessary: true,
      functional: true,
      analytics: false,
      marketing: false
    };
    const updated = buildSnapshot({ ...current, necessary: true, [category]: value });
    _snapshot.value = updated;
    writeStorage(updated);
    _showBanner.value = false;
  };

  const acceptAll = (): void => {
    const snap = buildSnapshot({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    });
    _snapshot.value = snap;
    writeStorage(snap);
    _showBanner.value = false;
    _showPreferences.value = false;
  };

  const rejectAll = (): void => {
    const snap = buildSnapshot({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
    _snapshot.value = snap;
    writeStorage(snap);
    _showBanner.value = false;
    _showPreferences.value = false;
  };

  const openPreferences = (): void => {
    _showPreferences.value = true;
    _showBanner.value = false;
  };

  const closePreferences = (): void => {
    _showPreferences.value = false;
  };

  const shouldShowBanner = computed(() => _showBanner.value);

  const shouldShowPreferences = computed(() => _showPreferences.value);

  return {
    hasDecided,
    hasConsent,
    snapshot,
    setConsent,
    acceptAll,
    rejectAll,
    openPreferences,
    shouldShowBanner,
    shouldShowPreferences,
    closePreferences
  };
};
